import { create } from 'zustand'

import type { UserRole, AccountStatus, LearningLevel, LearningGoal } from '@neuroforge/shared'

export interface AuthUser {
  id: string
  email: string
  username: string
  displayName: string
  avatarUrl?: string | null
  role: UserRole
  status: AccountStatus
  emailVerified: boolean
  locale: string
  learningLevel: LearningLevel
  goals: LearningGoal[]
  subscriptionTier?: string
  createdAt: Date
}

interface AuthState {
  accessToken: string | null
  user: AuthUser | null
  isAuthenticated: boolean
  isLoading: boolean
  authPending: boolean
  /** Monotonic: invalidates all responses started under an older auth context. */
  sessionEpoch: number

  // Existing public actions remain compatible.
  setAccessToken: (token: string) => void
  setUser: (user: AuthUser) => void
  logout: () => void
  setLoading: (loading: boolean) => void
  setAuthPending: (pending: boolean) => void
}

export const useAuthStore = create<AuthState>()((set) => ({
  accessToken: null,
  user: null,
  isAuthenticated: false,
  isLoading: false,
  authPending: false,
  sessionEpoch: 0,

  setAccessToken: (token) =>
    set((state) =>
      state.accessToken === token
        ? state
        : { accessToken: token, sessionEpoch: state.sessionEpoch + 1 },
    ),

  setUser: (user) =>
    set((state) => ({
      user,
      isAuthenticated: true,
      isLoading: false,
      authPending: false,
      // A server-side tier/role change also invalidates private media/responses.
      sessionEpoch:
        state.user &&
        (state.user.id !== user.id ||
          state.user.subscriptionTier !== user.subscriptionTier ||
          state.user.role !== user.role ||
          state.user.status !== user.status)
          ? state.sessionEpoch + 1
          : state.sessionEpoch,
    })),

  logout: () =>
    set((state) => ({
      accessToken: null,
      user: null,
      isAuthenticated: false,
      isLoading: false,
      authPending: false,
      sessionEpoch: state.sessionEpoch + 1,
    })),

  setLoading: (loading) => set({ isLoading: loading }),
  setAuthPending: (pending) =>
    set((state) =>
      state.authPending === pending
        ? state
        : {
            authPending: pending,
            sessionEpoch: pending ? state.sessionEpoch + 1 : state.sessionEpoch,
          },
    ),
}))

// Security migration: access tokens must not survive a reload.
if (typeof window !== 'undefined') {
  try {
    window.localStorage.removeItem('neuroforge-auth')
  } catch {
    // localStorage may be disabled.
  }
}
